import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
  DotsHorizontalIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  DownloadIcon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import {
  Box,
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Select,
  Spinner,
  Table,
  Text,
  TextField,
  Tooltip,
} from '@radix-ui/themes';
import { debounce, isEmpty } from 'lodash';
import toast from 'react-hot-toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useExportToExcel } from '../../hooks/useExportToExcel';
import { SEARCH_BY_BUTTON, SEARCH_BY_ENTER } from '../../utils/constants';
import CheckboxComponent from '../inputs/Checkbox/CheckboxComponent';
import { EmptyState } from './EmptyState';

interface Option {
  label: string;
  onClick: (item?: any) => void;
  icon?: any;
  component?: any;
}

interface ITableComponent {
  columns?: string[];
  items?: Array<{ [key: string]: any }>;
  options?: Option[][];
  columnNamesMap: { [key: string]: string };
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  filterFields?: any;
  totalColumns?: string[];
  onSearch?: (searchTerm: string) => void;
  search?: string;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  isFetching?: boolean;
  totalItems?: number;
  enableSearchByEnter?: boolean;
  enableSearchByBtn?: boolean;
  enableSearch?: boolean;
  enablePaginate?: boolean;
  control?: any;
  errors?: any;
  setValue?: any;
  getValues?: any;
  optionsForMassiveItems?: Option[];
  footerContent?: React.ReactNode;
  enableExportToExcel?: boolean;
  exportFileName?: string;
  /**
   * Devolve todos os registos do filtro actual para a exportação. Sem isto, a
   * exportação leva apenas a página que está no ecrã, que com páginas pequenas
   * significa um ficheiro truncado.
   */
  onExportAll?: () => Promise<Array<{ [key: string]: any }>>;
  /** Mensagem apresentada quando não há registos a mostrar. */
  emptyMessage?: string;
}

const TableComponent = ({
  columns = [],
  items = [],
  options = [],
  columnNamesMap,
  totalPages,
  currentPage,
  onPageChange,
  filterFields,
  totalColumns = [],
  onSearch,
  search = '',
  pageSize = 5,
  onPageSizeChange,
  isFetching = false,
  totalItems,
  enableSearchByBtn = SEARCH_BY_BUTTON,
  enableSearchByEnter = SEARCH_BY_ENTER,
  enableSearch = true,
  enablePaginate = true,
  control,
  errors,
  setValue,
  getValues,
  optionsForMassiveItems = [],
  footerContent = null,
  enableExportToExcel = false,
  exportFileName = `${new Date().toUTCString()}exporter.xlsx`,
  onExportAll,
  emptyMessage,
}: ITableComponent) => {
  const [searchTerm, setSearchTerm] = useState(search);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [isExporting, setIsExporting] = useState(false);
  const { exportToExcel } = useExportToExcel();

  // O onSearch chega como função nova em cada render do pai. Guardá-lo numa ref
  // mantém o debounce estável, senão era recriado a cada render e deixava de
  // agrupar as teclas (disparava uma pesquisa por letra).
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const debouncedSearch = useMemo(
    () => debounce((term: string) => onSearchRef.current?.(term), 900),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (enableSearchByEnter) return;
    if (enableSearchByBtn) return;
    if (onSearch) {
      debouncedSearch(value);
    }
  };

  const handlePageSizeChange = (value: string) => {
    const size = parseInt(value, 10);
    setCurrentPageSize(size);
    if (onPageSizeChange) {
      onPageSizeChange(size);
    }
  };

  const filteredItems = onSearch
    ? items
    : searchTerm
      ? items.filter((item) =>
        columns.some((column) =>
          item[column]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
      : items;

  const totals: { [key: string]: number } = {};

  if (totalColumns.length > 0) {
    totalColumns.forEach((column) => {
      totals[column] = filteredItems.reduce((sum, item) => sum + (item[column] || 0), 0);
    });
  }

  const handleSearchByPressingEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (enableSearchByEnter) {
      event.key === 'Enter' && enableSearchByEnter && onSearch && onSearch(searchTerm);
    }
  }

  // Repesquisa quando o utilizador limpa a caixa de pesquisa.
  // Não dispara na montagem: a tabela é remontada sempre que a query recarrega
  // (o Lists troca a tabela pelo skeleton), e um onSearch('') nessa altura
  // chamava o handleSearch do pai, que faz setCurrentPage(1) — era isto que
  // fazia a listagem voltar à primeira página ao mudar de página.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isEmpty(searchTerm)) {
      onSearchRef.current?.(searchTerm);
    }
  }, [searchTerm])

  const handleCheckBoxSelectAll = (checked: boolean) => {
    filteredItems.map((item, index) => {
      setValue(`item.${index}`, checked)
    });
  }

  const handleOnclickBtnMassiveItems = (option: Option) => {
    const selectedItems = filteredItems.filter((item, index) => {
      const itemIsChecked = getValues(`item.${index}`);
      return itemIsChecked;
    });

    option.onClick(selectedItems);
    filteredItems.map((item, index) => {
      setValue(`item.${index}`, false)
    });
    setValue('selectAll', false)
  };

  const hasRows = filteredItems.length > 0;

  const mapRowsForExcel = (rows: Array<{ [key: string]: any }>) =>
    rows.map((item) => {
      const newItem: { [key: string]: any } = {};
      columns.forEach((column) => {
        const header = columnNamesMap[column] || column;
        newItem[header] = item[column];
      });
      return newItem;
    });

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      // Com o onExportAll a exportação leva todos os registos do filtro;
      // sem ele, leva a página visível (o comportamento antigo).
      const rows = onExportAll ? await onExportAll() : filteredItems;
      exportToExcel(mapRowsForExcel(rows), exportFileName);
    } catch (error) {
      toast.error('Não foi possível exportar todos os registos.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Flex direction="column" gap="4" wrap="nowrap">
      <Flex justify="between" align="center" gap="4" wrap="wrap">
        <Flex gap="2" align="center" wrap="wrap">
          {enableSearch && (
            <TextField.Root
              size="2"
              onKeyDown={handleSearchByPressingEnter}
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Pesquisar"
              aria-label="Pesquisar na tabela"
            >
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
              {searchTerm && (
                <TextField.Slot>
                  <IconButton
                    type="button"
                    size="1"
                    variant="ghost"
                    color="gray"
                    aria-label="Limpar pesquisa"
                    onClick={() => setSearchTerm('')}
                  >
                    <Cross2Icon height="14" width="14" />
                  </IconButton>
                </TextField.Slot>
              )}
            </TextField.Root>
          )}
          {enableSearchByBtn && (
            <Tooltip content="Pesquisar">
              <IconButton
                type="button"
                aria-label="Pesquisar"
                onClick={() => onSearch && onSearch(searchTerm)}
              >
                <MagnifyingGlassIcon height="16" width="16" />
              </IconButton>
            </Tooltip>
          )}
          {enableExportToExcel && (
            <Button
              type="button"
              variant="soft"
              disabled={!hasRows || isExporting}
              onClick={handleExportToExcel}
            >
              {isExporting ? <Spinner size="1" /> : <DownloadIcon />} Exportar para Excel
            </Button>
          )}
          {control && optionsForMassiveItems.length > 0 && optionsForMassiveItems.map((option, index) => (
            <Button type="button" key={index} onClick={() => handleOnclickBtnMassiveItems(option)} variant="soft">
              {option.icon} {option.label}
            </Button>
          ))}
        </Flex>

        <Flex gap="2" align="center" wrap="wrap">
          {filterFields && filterFields()}
        </Flex>

        <Flex gap="3" align="center" wrap="wrap">
          {isFetching && <Spinner size="1" />}
          {totalItems !== undefined && (
            <Text size="2" color="gray">Total: {totalItems}</Text>
          )}
          {onPageSizeChange && (
            <Flex gap="2" align="center">
              <Text size="1" color="gray">Por página</Text>
              <Select.Root value={currentPageSize.toString()} onValueChange={handlePageSizeChange}>
                <Select.Trigger aria-label="Registos por página" />
                <Select.Content>
                  <Select.Item value="10">10</Select.Item>
                  <Select.Item value="20">20</Select.Item>
                  <Select.Item value="50">50</Select.Item>
                  <Select.Item value="100">100</Select.Item>
                  <Select.Item value="300">300</Select.Item>
                  <Select.Item value="500">500</Select.Item>
                  <Select.Item value="1000">1000</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
          )}
        </Flex>
      </Flex>

      {!hasRows && !isFetching ? (
        <EmptyState
          title="Sem registos"
          description={
            emptyMessage ??
            (searchTerm
              ? 'A pesquisa não devolveu resultados. Tente outros termos.'
              : 'Ainda não existem dados para apresentar.')
          }
        />
      ) : (
        <div className="table-wrap" style={{ overflowX: 'auto', overflowY: 'hidden', width: '100%' }}>
          <Table.Root variant="surface" size="2">
            <Table.Header>
              {totalColumns.length > 0 && (
                <Table.Row>
                  {control && <Table.ColumnHeaderCell />}
                  {columns.map((column) => (
                    <Table.ColumnHeaderCell key={column}>
                      <Text weight="bold">{totalColumns.includes(column) ? totals[column] : ''}</Text>
                    </Table.ColumnHeaderCell>
                  ))}
                  {options.length > 0 && <Table.ColumnHeaderCell />}
                </Table.Row>
              )}
              <Table.Row>
                {control && (
                  <Table.ColumnHeaderCell>
                    <CheckboxComponent
                      name="selectAll"
                      label=""
                      ariaLabel="Seleccionar todos os registos"
                      control={control || undefined}
                      errors={errors || {}}
                      onChange={handleCheckBoxSelectAll}
                    />
                  </Table.ColumnHeaderCell>
                )}
                {columns.map((column) => (
                  <Table.ColumnHeaderCell key={column} style={{ whiteSpace: 'nowrap' }}>
                    {columnNamesMap[column] || column}
                  </Table.ColumnHeaderCell>
                ))}
                {options.length > 0 && (
                  <Table.ColumnHeaderCell>
                    <Flex justify="end">Operações</Flex>
                  </Table.ColumnHeaderCell>
                )}
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {filteredItems.map((item, index) => (
                <Table.Row key={index} align="center">
                  {control && (
                    <Table.Cell>
                      <CheckboxComponent
                        name={`item.${index}`}
                        label=""
                        ariaLabel={`Seleccionar registo ${index + 1}`}
                        control={control || undefined}
                        errors={errors || {}}
                      />
                    </Table.Cell>
                  )}
                  {columns.map((column) => (
                    <Table.Cell key={column}>
                      {item[column] === null || item[column] === undefined || item[column] === ''
                        ? <Text color="gray">—</Text>
                        : item[column]}
                    </Table.Cell>
                  ))}
                  {options.length > 0 && (
                    <Table.Cell>
                      <Flex justify="end" pr="4">
                        <DropdownMenu.Root>
                          <Tooltip content="Operações">
                            <DropdownMenu.Trigger>
                              <IconButton
                                type="button"
                                variant="soft"
                                color="gray"
                                aria-label={`Operações do registo ${index + 1}`}
                              >
                                <DotsHorizontalIcon />
                              </IconButton>
                            </DropdownMenu.Trigger>
                          </Tooltip>
                          <DropdownMenu.Content>
                            {options[index]?.map((option, i) => (
                              <DropdownMenu.Item key={i} onSelect={() => option.onClick(item)}>
                                {option?.icon}
                                {option.label}
                              </DropdownMenu.Item>
                            ))}
                          </DropdownMenu.Content>
                        </DropdownMenu.Root>
                      </Flex>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}

              {totalColumns.length > 0 && (
                <Table.Row>
                  {control && <Table.Cell />}
                  {columns.map((column) => (
                    <Table.Cell key={column}>
                      <Text weight="bold">{totalColumns.includes(column) ? totals[column] : ''}</Text>
                    </Table.Cell>
                  ))}
                  {options.length > 0 && <Table.Cell />}
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </div>
      )}

      {footerContent && (
        <Box mt="4">{footerContent}</Box>
      )}

      {enablePaginate && totalPages > 1 && (
        <Flex justify="center" align="center" gap="2" mt="4" wrap="wrap">
          <Tooltip content="Primeira página">
            <IconButton
              type="button"
              variant="soft"
              color="gray"
              aria-label="Primeira página"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(1)}
            >
              <DoubleArrowLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content="Página anterior">
            <IconButton
              type="button"
              variant="soft"
              aria-label="Página anterior"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>

          <Text size="2" color="gray" mx="2">
            Página <Text weight="bold" color="gray">{currentPage}</Text> de {totalPages}
          </Text>

          <Tooltip content="Página seguinte">
            <IconButton
              type="button"
              variant="soft"
              aria-label="Página seguinte"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          <Tooltip content="Última página">
            <IconButton
              type="button"
              variant="soft"
              color="gray"
              aria-label="Última página"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              <DoubleArrowRightIcon />
            </IconButton>
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
};

export default TableComponent;
